/**
 * LoanLockFormV2 — request / confirm / manage surface for a loan's rate lock,
 * rebuilt to match the carded "Sheet" mockup: top-aligned labels, rounded
 * inputs, segmented toggles, dividers, soft tone callouts, and a left-ghost /
 * right-primary footer. The original LoanLockForm is kept intact as a fallback.
 *
 * Visuals are hand-rolled (no RFF/MUI TextField chrome) but every backend
 * contract is preserved exactly:
 *   • percent fields store a DECIMAL fraction (UI shows value × 100 with "%")
 *   • date fields store an ISO UTC-midnight string
 *   • payload shapes / endpoints are identical to the original form
 *
 * STATUS-DRIVEN off the snake_case lock mirror (deal.loanLock):
 *   • no active lock → REQUEST (price inputs only; the requester never sets
 *     counterparty/expiration). A sizer (upload or existing doc) is required.
 *     The request / update / desk-edit form is LockRequestForm, on the panel
 *     form standard; the `frame` prop lets the sidebar pin its footer.
 *   • Requested → CONFIRM / RETURN (capital-markets desk).
 *   • Confirmed/Repriced → MANAGE (extend, cancel, pricing exception).
 *
 * Endpoints return the lock doc (not the deal), so on success we refetch the
 * loan via getLoanById and re-hydrate the drilldown.
 */
import { isValidElement, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import {
  BoltOutlined,
  CheckCircleOutlined,
  FlagOutlined,
  LockOutlined,
  SendOutlined,
  LockClockOutlined,
  ShieldOutlined,
  UndoOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import isNil from "lodash/isNil";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import {
  DATE_INPUT_MAX,
  useDateInputDraft,
} from "../../../../components/forms/FormPrimitives";
import { usePermissionsHook } from "../../../../hooks/usePermissionsHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { useCorrespondentsApi } from "../../../../hooks/useCorrespondentsHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import {
  DSCR_MARGIN_TIERS,
  LOCK_CHANNEL_OPTIONS,
  RTL_BORROWER_CLASSIFICATIONS,
  defaultLockChannelForDeal,
  lockChannelOf,
  losLocksApi,
} from "../../../../api/losLocksApi";
import { getLockProduct, getLockStage } from "./loanLockUtils";
import LockRequestForm from "./LockRequestForm";
import { ink, slate, status, teal } from "../../../../theme/colors";

/* ── design tokens — the Eastview color system (src/theme/colors.js) ────────
   Teal is the action/accent family: teal-600 under white labels, teal-700 for
   text on a tint, teal-50/200 for the tint + its border. Status colors come
   from `status`; their hairlines are the status ink at low alpha. */
const T = {
  ink: ink,
  slate: slate[600],
  muted: slate[400],
  faint: slate[300],
  line: slate[200],
  accent: teal[600],
  accentSoft: teal[50],
  accentLine: teal[200],
  accentInk: teal[700],
  amber: status.warning.fg,
  amberSoft: status.warning.bg,
  amberLine: `${status.warning.fg}40`,
  green: status.success.solid,
  greenSoft: status.success.bg,
  greenLine: `${status.success.fg}40`,
  greenInk: status.success.fg,
  rose: status.error.fg,
  // actionNeeded ("Returned for action") — shared red, consistent with the Loan
  // Lock tab and the lock queue.
  red: status.error.solid,
  redSoft: status.error.bg,
  redLine: `${status.error.fg}40`,
  redInk: status.error.fg,
};

const LOCK_PERIODS = [15, 30, 45, 60];

/* ── value <-> string helpers (preserve backend formats) ─────────────────── */
// Percent fields store a decimal fraction; the UI edits the percent string.
const decToPctStr = (dec) =>
  isNil(dec) || dec === "" ? "" : String(+(Number(dec) * 100).toFixed(3));
const pctStrToDec = (str) => {
  const n = parseFloat(str);
  return isNaN(n) ? null : Number((n / 100).toFixed(5));
};
const numStrToNum = (str) => {
  const n = parseFloat(str);
  return isNaN(n) ? null : n;
};
const todayIso = () => {
  const d = new Date();
  return new Date(
    Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0),
  ).toISOString();
};
// Add N days to an ISO date, returning an ISO UTC-midnight string.
const addDaysIso = (iso, days) => {
  const base = iso ? new Date(iso) : null;
  if (!base || isNaN(base.getTime()) || !Number.isFinite(days)) return iso;
  return new Date(
    Date.UTC(
      base.getUTCFullYear(),
      base.getUTCMonth(),
      base.getUTCDate() + days,
      0,
      0,
      0,
      0,
    ),
  ).toISOString();
};
const optVal = (o) => (typeof o === "string" ? o : o?.value);
const optLabel = (o) => (typeof o === "string" ? o : o?.label);

/* ── primitives (mockup) ─────────────────────────────────────────────────── */
const SectionLabel = ({ children, badge, sx }) => (
  <Box
    sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: "14px", ...sx }}
  >
    <Box
      sx={{
        fontSize: 11.5,
        fontWeight: 700,
        color: T.muted,
        textTransform: "uppercase",
        letterSpacing: 0.7,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
    {badge}
  </Box>
);

// The lock's `lock_notes`, read-only, at the top of the confirm / manage views.
const LockNotesCard = ({ notes }) => (
  <Box sx={{ mb: "20px" }}>
    <SectionLabel sx={{ mb: "8px" }}>Lock notes</SectionLabel>
    <Box
      sx={{
        fontSize: 14,
        fontWeight: notes ? 500 : 400,
        lineHeight: 1.55,
        color: notes ? T.ink : T.muted,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        // Gray box; the larger, heavier text makes filled notes stand out.
        background: slate[50],
        border: `1px solid ${T.line}`,
        borderRadius: "10px",
        p: "12px 14px",
      }}
    >
      {notes || "No lock notes."}
    </Box>
  </Box>
);

const Divider = () => (
  <Box sx={{ height: "1px", background: T.line, my: "26px" }} />
);

const Row = ({ children, sx }) => (
  <Box
    sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", ...sx }}
  >
    {children}
  </Box>
);

const Field = ({ label, hint, children, sx }) => (
  <Box sx={{ display: "block", ...sx }}>
    <Box sx={{ fontSize: 12.5, fontWeight: 600, color: T.slate, mb: "7px" }}>
      {label}
    </Box>
    {children}
    {hint && (
      <Box sx={{ fontSize: 11.5, color: T.muted, mt: "6px", lineHeight: 1.4 }}>
        {hint}
      </Box>
    )}
  </Box>
);

const inputSx = {
  width: "100%",
  boxSizing: "border-box",
  p: "10px 12px",
  fontSize: 14,
  border: `1px solid ${T.line}`,
  borderRadius: "10px",
  outline: "none",
  color: T.ink,
  background: "#fff",
  fontFamily: "inherit",
  "&::placeholder": { color: T.muted },
  "&:focus": { borderColor: T.accent, boxShadow: `0 0 0 3px ${T.accentSoft}` },
};

const TextInput = (props) => <Box component="input" sx={inputSx} {...props} />;

const TextArea = ({ minHeight = 76, sx, ...props }) => (
  <Box
    component="textarea"
    sx={{ ...inputSx, minHeight, resize: "vertical", ...sx }}
    {...props}
  />
);

const Select = ({ children, muted, sx, ...props }) => (
  <Box
    component="select"
    sx={{
      ...inputSx,
      color: muted ? T.muted : T.ink,
      cursor: "pointer",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Box>
);

// Percent input: edits the percent string, stores a decimal fraction upstream.
const PercentInput = ({ value, onChange, placeholder = "0.000%" }) => (
  <Box
    component="input"
    inputMode="decimal"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    sx={inputSx}
  />
);

// Drafted so a half-typed year ("0002") is never committed as 1902.
const DateInput = ({ value, onChange }) => {
  const [draft, handleChange] = useDateInputDraft(value, onChange);
  return (
    <Box
      component="input"
      type="date"
      max={DATE_INPUT_MAX}
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      sx={inputSx}
    />
  );
};

const Segmented = ({ options, value, onChange, full }) => (
  <Box
    sx={{
      display: full ? "grid" : "inline-flex",
      gridTemplateColumns: full ? `repeat(${options.length}, 1fr)` : undefined,
      background: slate[100],
      p: "3px",
      borderRadius: "10px",
      gap: "3px",
    }}
  >
    {options.map((o) => {
      const sel = value === o.value;
      return (
        <Box
          component="button"
          type="button"
          key={String(o.value)}
          onClick={() => onChange(o.value)}
          sx={{
            border: "none",
            cursor: "pointer",
            p: "7px 14px",
            borderRadius: "8px",
            fontSize: 13,
            fontWeight: 600,
            textAlign: "center",
            fontFamily: "inherit",
            background: sel ? "#fff" : "transparent",
            color: sel ? T.ink : T.slate,
            boxShadow: sel ? "0 1px 2px rgba(15,23,42,.08)" : "none",
          }}
        >
          {o.label}
        </Box>
      );
    })}
  </Box>
);

const Callout = ({ children, tone = "info" }) => {
  const map = {
    info: {
      bg: T.accentSoft,
      bd: T.accentLine,
      fg: T.accentInk,
      Icon: BoltOutlined,
    },
    warn: {
      bg: T.amberSoft,
      bd: T.amberLine,
      fg: T.amber,
      Icon: WarningAmberOutlined,
    },
    success: {
      bg: T.greenSoft,
      bd: T.greenLine,
      fg: T.greenInk,
      Icon: CheckCircleOutlined,
    },
  };
  const c = map[tone] || map.info;
  const { Icon } = c;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        background: c.bg,
        border: `1px solid ${c.bd}`,
        borderRadius: "12px",
        p: "12px 14px",
        maxWidth: "100%",
        boxSizing: "border-box",
        "& svg": { fontSize: 16, flexShrink: 0, mt: "1px" },
      }}
    >
      <Icon sx={{ color: c.fg }} />
      <Box
        sx={{
          // flex:1 1 0 (not auto) so the text box never sizes to its single-line
          // max-content — it takes the remaining width and the text wraps.
          flex: "1 1 0%",
          minWidth: 0,
          fontSize: 13,
          color: c.fg,
          lineHeight: 1.5,
          whiteSpace: "normal",
          overflowWrap: "anywhere",
          wordBreak: "break-word",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

// actionNeeded ("Returned for action") — the shared red treatment used on the
// Loan Lock tab and the lock queue. Title + optional "Exception required" pill +
// reason + flagged-by/date.
const ActionNeededCallout = ({ action }) => {
  const flaggedMeta = [
    action.flaggedBy ? `Flagged by ${action.flaggedBy}` : null,
    action.flaggedDateTime ? formatDate(action.flaggedDateTime) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        background: T.redSoft,
        border: `1px solid ${T.redLine}`,
        borderRadius: "12px",
        p: "12px 14px",
        "& svg": { fontSize: 16, flexShrink: 0, mt: "1px" },
      }}
    >
      <FlagOutlined sx={{ color: T.red }} />
      <Box sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ fontSize: 13, fontWeight: 700, color: T.red }}>
            Returned for action
          </Box>
          {action.requiresException && (
            <Box
              component="span"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: T.red,
                background: "#fff",
                border: `1px solid ${T.redLine}`,
                px: 0.75,
                py: "1px",
                borderRadius: "999px",
              }}
            >
              Exception required
            </Box>
          )}
        </Box>
        <Box sx={{ fontSize: 13, color: T.redInk, lineHeight: 1.5, mt: 0.5 }}>
          {action.reason}
        </Box>
        {flaggedMeta && (
          <Box sx={{ fontSize: 11.5, color: T.redInk, opacity: 0.85, mt: 0.5 }}>
            {flaggedMeta}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// A lock extension fee is stored in basis points (bps). Show it compactly.
const formatBps = (bps) =>
  isNil(bps) || bps === "" ? "—" : `${+Number(bps).toFixed(2)} bps`;

/* ════════════════════════════════════════════════════════════════
   PENDING EXTENSION — set by request_lock_extension, applied/denied by
   confirm_lock_extension (capital markets). Shows the requested terms +
   computed fee; the desk confirms (optionally overriding the fee) or denies.
   ════════════════════════════════════════════════════════════════ */
const PendingExtensionCallout = ({
  pending,
  canConfirm,
  busy,
  onConfirm,
  onDeny,
}) => {
  const [feeOverride, setFeeOverride] = useState(
    isNil(pending?.fee) ? "" : String(pending.fee),
  );
  const [denying, setDenying] = useState(false);
  const [denyReason, setDenyReason] = useState("");
  const anyBusy = busy != null;

  const meta = [
    pending?.requestedBy ? `Requested by ${pending.requestedBy}` : null,
    pending?.requestDateTime ? formatDate(pending.requestDateTime) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const rows = [
    pending?.extensionDays != null
      ? ["Extension", `${pending.extensionDays} days`]
      : null,
    pending?.newExpiration
      ? ["New expiration", formatDate(pending.newExpiration)]
      : null,
    ["Fee", formatBps(pending?.fee)],
    pending?.reason ? ["Reason", pending.reason] : null,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        background: T.amberSoft,
        border: `1px solid ${T.amberLine}`,
        borderRadius: "12px",
        p: "12px 14px",
        "& svg": { fontSize: 16, flexShrink: 0, mt: "1px" },
      }}
    >
      <LockClockOutlined sx={{ color: T.amber }} />
      <Box sx={{ flex: "1 1 0%", minWidth: 0 }}>
        <Box sx={{ fontSize: 13, fontWeight: 700, color: T.amber }}>
          Pending extension — awaiting confirmation
        </Box>
        <Box
          sx={{
            mt: 1,
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            columnGap: "12px",
            rowGap: "4px",
            fontSize: 12.5,
          }}
        >
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ display: "contents" }}>
              <Box sx={{ color: T.slate, fontWeight: 600 }}>{label}</Box>
              <Box sx={{ color: T.ink, overflowWrap: "anywhere" }}>{value}</Box>
            </Box>
          ))}
        </Box>
        {meta && (
          <Box sx={{ fontSize: 11.5, color: T.slate, mt: 0.75 }}>{meta}</Box>
        )}

        {canConfirm ? (
          denying ? (
            <Box sx={{ mt: "14px" }}>
              <Field label="Denial reason (optional)">
                <TextInput
                  placeholder="Why is this extension being denied?"
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                />
              </Field>
              <Box sx={{ display: "flex", gap: 1.25, mt: "12px" }}>
                <GhostBtn
                  disabled={anyBusy}
                  onClick={() => {
                    setDenying(false);
                    setDenyReason("");
                  }}
                >
                  Back
                </GhostBtn>
                <PrimaryBtn
                  color={T.rose}
                  loading={busy === "denyExt"}
                  disabled={anyBusy && busy !== "denyExt"}
                  onClick={() => onDeny({ reason: denyReason || undefined })}
                >
                  Deny extension
                </PrimaryBtn>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: "14px" }}>
              <Field
                label="Confirm fee (bps)"
                hint="Overrides the computed fee. Leave as-is to accept it."
              >
                <TextInput
                  type="number"
                  value={feeOverride}
                  onChange={(e) => setFeeOverride(e.target.value)}
                />
              </Field>
              <Box sx={{ display: "flex", gap: 1.25, mt: "12px" }}>
                <GhostBtn disabled={anyBusy} onClick={() => setDenying(true)}>
                  Deny
                </GhostBtn>
                <PrimaryBtn
                  color={T.green}
                  loading={busy === "confirmExt"}
                  disabled={anyBusy && busy !== "confirmExt"}
                  onClick={() =>
                    onConfirm(
                      feeOverride === "" || isNil(feeOverride)
                        ? {}
                        : { fee: Number(feeOverride) },
                    )
                  }
                >
                  Confirm extension
                </PrimaryBtn>
              </Box>
            </Box>
          )
        ) : (
          <Box
            sx={{
              fontSize: 12,
              color: T.slate,
              mt: "10px",
              fontStyle: "italic",
            }}
          >
            The capital-markets desk will confirm or deny this extension.
          </Box>
        )}
      </Box>
    </Box>
  );
};

const fmtPts = (n) =>
  isNil(n) || n === "" ? "—" : `${+Number(n).toFixed(3)} pts`;

// RTL product taxonomy (for resolving the deal's product row, incl. Heavy Rehab).
const RTL_PRODUCT_LABELS = {
  fix_and_flip: "Fix and Flip",
  fix_and_flip_heavy_rehab: "Fix and Flip — Heavy Rehab",
  ground_up_construction: "Ground Up Construction",
  bridge: "Bridge",
};

// Map the loan's product type (+ heavy-rehab flag) to an rtl_base_rates product
// key — mirrors the backend rtl_product_key_for().
const rtlProductKeyFor = (loanProductType, heavyRehab) => {
  const s = (loanProductType || "").trim().toLowerCase();
  if (!s) return null;
  if (s.includes("ground") || s === "guc" || s === "gu")
    return "ground_up_construction";
  if (s.includes("bridge")) return "bridge";
  if (s.includes("fix") && s.includes("flip"))
    return heavyRehab || s.includes("heavy") || s.includes("rehab")
      ? "fix_and_flip_heavy_rehab"
      : "fix_and_flip";
  if (s.includes("heavy") || s.includes("rehab"))
    return "fix_and_flip_heavy_rehab";
  return null;
};

/* ════════════════════════════════════════════════════════════════
   CORRESPONDENT PRICING NOTE — read-only context resolved from the loan's
   correspondent (effectivePricing): DSCR effective margin (tier + additional /
   override), the resolved RTL product + spread, the max-lock window, and the
   extension-fee schedule. Final price still resolves server-side at lock time.
   ════════════════════════════════════════════════════════════════ */
// Small bordered pill for the footer meta (max lock, extension fee).
const MetaPill = ({ label, value }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "baseline",
      gap: 0.5,
      fontSize: 11.5,
      background: slate[100],
      border: `1px solid ${T.line}`,
      borderRadius: "8px",
      px: 1,
      py: "3px",
    }}
  >
    <Box component="span" sx={{ color: T.slate, fontWeight: 600 }}>
      {label}
    </Box>
    <Box
      component="span"
      sx={{ color: T.ink, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
    >
      {value}
    </Box>
  </Box>
);

const CorrespondentPricingNote = ({
  product,
  corrPricing,
  effectiveDscrMargin,
  dscrTier,
  dscrAdditional,
  dscrOverride,
  rtlProductKey,
}) => {
  if (!corrPricing) return null;
  const isRtl = product === "rtl";

  // Headline metric + supporting line + the right-aligned tier/product chip.
  let chip = null;
  let featuredLabel = "";
  let featuredValue = "—";
  let sublabel = "";
  let adjusted = false;

  if (isRtl) {
    const spread = corrPricing?.rtl_pricing_spreads?.[rtlProductKey];
    chip = RTL_PRODUCT_LABELS[rtlProductKey] || rtlProductKey || null;
    featuredLabel = "Correspondent spread";
    featuredValue =
      spread == null ? "—" : `${+(Number(spread) * 100).toFixed(4)}%`;
    sublabel = "Buy rate = base rate (by term) + spread, resolved at lock.";
  } else {
    adjusted = dscrOverride != null || Number(dscrAdditional || 0) !== 0;
    chip = dscrTier || null;
    featuredLabel = "Effective DSCR margin";
    featuredValue =
      effectiveDscrMargin == null ? "—" : fmtPts(effectiveDscrMargin);
    if (dscrOverride != null) {
      sublabel = "Negotiated override — replaces the tier margin.";
    } else if (effectiveDscrMargin != null && dscrTier) {
      const add = Number(dscrAdditional || 0);
      const baseTier = effectiveDscrMargin - add;
      sublabel =
        add !== 0
          ? `${dscrTier} ${fmtPts(baseTier)} + ${fmtPts(add)} additional`
          : `${dscrTier} tier margin`;
    } else {
      sublabel = "Resolves from the loan's correspondent at lock.";
    }
  }

  const metas = [];
  if (corrPricing.max_lock_days != null) {
    metas.push(["Max lock", `${corrPricing.max_lock_days} days`]);
  }
  const bpsPerDay =
    corrPricing.lock_extension_fee_bps_per_day == null
      ? 2.0
      : corrPricing.lock_extension_fee_bps_per_day;
  metas.push(["Extension fee", `${+Number(bpsPerDay).toFixed(2)} bps/day`]);

  return (
    <Box
      sx={{
        background: "#fff",
        border: `1px solid ${T.line}`,
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
    >
      {/* header — label + tier/product chip */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: "14px",
          py: "9px",
          borderBottom: `1px solid ${T.line}`,
          background: slate[50],
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ShieldOutlined sx={{ fontSize: 15, color: T.accent }} />
          <Box
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: T.slate,
            }}
          >
            Correspondent pricing
          </Box>
        </Box>
        {chip && (
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: T.accentInk,
              background: T.accentSoft,
              border: `1px solid ${T.accentLine}`,
              borderRadius: "999px",
              px: 1,
              py: "2px",
              whiteSpace: "nowrap",
            }}
          >
            {chip}
          </Box>
        )}
      </Box>

      {/* featured metric */}
      <Box sx={{ px: "14px", pt: "11px", pb: metas.length ? "10px" : "12px" }}>
        <Box sx={{ fontSize: 11.5, color: T.slate, fontWeight: 600 }}>
          {featuredLabel}
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: "1px" }}
        >
          <Box
            sx={{
              fontSize: 22,
              fontWeight: 800,
              lineHeight: 1.15,
              color: adjusted ? T.amber : T.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {featuredValue}
          </Box>
          {adjusted && (
            <Box
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: T.amber,
                background: T.amberSoft,
                border: `1px solid ${T.amberLine}`,
                borderRadius: "999px",
                px: 0.75,
                py: "1px",
              }}
            >
              Adjusted
            </Box>
          )}
        </Box>
        {sublabel && (
          <Box
            sx={{
              fontSize: 11.5,
              color: T.muted,
              mt: "4px",
              lineHeight: 1.5,
              overflowWrap: "anywhere",
            }}
          >
            {sublabel}
          </Box>
        )}
      </Box>

      {/* footer meta pills */}
      {metas.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            px: "14px",
            pb: "12px",
          }}
        >
          {metas.map(([label, value]) => (
            <MetaPill key={label} label={label} value={value} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const DeskBadge = () => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      background: T.accentSoft,
      color: T.accent,
      border: `1px solid ${T.accentLine}`,
      borderRadius: "999px",
      px: 1,
      py: 0.25,
      fontSize: 10.5,
      fontWeight: 700,
      "& svg": { fontSize: 12 },
    }}
  >
    <ShieldOutlined /> Capital markets
  </Box>
);

const baseBtnSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  p: "10px 18px",
  borderRadius: "10px",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  whiteSpace: "nowrap",
  "& svg": { fontSize: 15 },
  "&:disabled": { opacity: 0.5, cursor: "default" },
};

const PrimaryBtn = ({
  children,
  onClick,
  color = T.accent,
  loading,
  disabled,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    sx={{
      ...baseBtnSx,
      background: color,
      flexGrow: 1,
      color: "#fff",
      border: "none",
      "&:hover": { filter: "brightness(0.94)" },
    }}
  >
    {loading && <CircularProgress size={14} sx={{ color: "#fff" }} />}
    {children}
  </Box>
);

const GhostBtn = ({ children, onClick, disabled }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      ...baseBtnSx,
      background: "#fff",
      color: T.slate,
      border: `1px solid ${T.line}`,
      "&:hover": { background: slate[50] },
    }}
  >
    {children}
  </Box>
);

// Footer bar: left (ghost) / right (primary), separated by a top border.
const Footer = ({ left, right }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 1.25,
      mt: "26px",
      pt: "16px",
      borderTop: `1px solid ${T.line}`,
    }}
  >
    {left || <span />}
    {right}
  </Box>
);

/* ════════════════════════════════════════════════════════════════
   CONFIRM / RETURN (capital-markets desk) — tabbed like the mockup
   ════════════════════════════════════════════════════════════════ */
const ConfirmReturnForm = ({
  lock,
  loanDrilldown,
  onConfirm,
  onReturn,
  onCancel,
  busy,
}) => {
  const [tab, setTab] = useState("confirm"); // "confirm" | "return"

  // confirm fields
  const initialPeriod = lock?.lock_period_days ?? 30;
  const initialConfirmDate = todayIso(); // defaults to today
  const [period, setPeriod] = useState(initialPeriod);
  const [confirmDate, setConfirmDate] = useState(initialConfirmDate);
  // Expiration is always shown and defaults to confirm date + period; it stays in
  // sync as the period / confirm date change, but can be hand-edited (which flips
  // the period to "Other" so the explicit date is what gets sent).
  const [expirationDate, setExpirationDate] = useState(() =>
    addDaysIso(initialConfirmDate, Number(initialPeriod)),
  );
  // The requester already set the channel; the desk can correct it on confirm.
  const [lockChannel, setLockChannel] = useState(
    () => lockChannelOf(lock) ?? defaultLockChannelForDeal(loanDrilldown),
  );
  const [confirmNotes, setConfirmNotes] = useState("");

  const handlePeriodChange = (v) => {
    setPeriod(v);
    if (v !== "other") setExpirationDate(addDaysIso(confirmDate, Number(v)));
  };
  const handleConfirmDateChange = (v) => {
    setConfirmDate(v);
    if (period !== "other") setExpirationDate(addDaysIso(v, Number(period)));
  };
  const handleExpirationChange = (v) => {
    // Hand-editing the expiration means a custom date — drop the preset period.
    setExpirationDate(v);
    setPeriod("other");
  };

  // return fields
  const [reason, setReason] = useState("");
  const [requiresException, setRequiresException] = useState(false);

  const submitConfirm = () => {
    // Counterparty is NOT set here (trade-counterparty barrier) — confirm only
    // finalizes expiration.
    const payload = {};
    if (period === "other") {
      if (expirationDate) payload.lock_expiration_date = expirationDate;
    } else if (period) {
      payload.lock_period_days = Number(period);
    }
    if (confirmDate) payload.lock_confirm_date = confirmDate;
    payload.lock_channel = lockChannel;
    // The backend REPLACES lock_notes with whatever is sent, so append the
    // desk's note to the requester's rather than wiping it.
    const note = confirmNotes.trim();
    if (note) {
      const existing = (lock?.lock_notes || "").trim();
      payload.lock_notes = existing ? `${existing}\n${note}` : note;
    }
    onConfirm(payload);
  };

  if (tab === "return") {
    return (
      <Box>
        <SectionLabel>Return to requester</SectionLabel>
        <Callout tone="warn">
          Sends the lock back. The requester is emailed to update terms or file
          a pricing exception.
        </Callout>
        <Box sx={{ mt: "20px" }}>
          <Field label="Reason">
            <TextArea
              minHeight={100}
              placeholder="Why is this being returned?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </Box>
        <Box
          component="label"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mt: "16px",
            fontSize: 13.5,
            color: T.slate,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={requiresException}
            onChange={(e) => setRequiresException(e.target.checked)}
          />
          Requires a pricing-exception request
        </Box>
        <Footer
          left={<GhostBtn onClick={() => setTab("confirm")}>Back</GhostBtn>}
          right={
            <PrimaryBtn
              color={T.amber}
              loading={busy === "return"}
              disabled={(busy && busy !== "return") || !reason.trim()}
              onClick={() => onReturn({ reason, requiresException })}
            >
              Send back
              <SendOutlined className="thin" />
            </PrimaryBtn>
          }
        />
      </Box>
    );
  }

  return (
    <Box>
      <SectionLabel badge={<DeskBadge />}>
        Counterparty &amp; expiration
      </SectionLabel>
      <Field
        label="Lock period"
        hint="Sets the expiration below. Pick Other to enter an explicit date."
      >
        <Segmented
          full
          options={[
            ...LOCK_PERIODS.map((d) => ({ value: d, label: String(d) })),
            { value: "other", label: "Other" },
          ]}
          value={period}
          onChange={handlePeriodChange}
        />
      </Field>
      <Box sx={{ mt: "24px" }}>
        <Field
          label="Expiration date"
          hint={
            period === "other"
              ? "Explicit expiration date."
              : `Confirmation date + ${period} days. Edit to override.`
          }
        >
          <DateInput value={expirationDate} onChange={handleExpirationChange} />
        </Field>
      </Box>
      <Row sx={{ mt: "24px" }}>
        <Field label="Confirmation date" hint="Defaults to today.">
          <DateInput value={confirmDate} onChange={handleConfirmDateChange} />
        </Field>
        <Field label="Lock type">
          <Segmented
            options={LOCK_CHANNEL_OPTIONS}
            value={lockChannel}
            onChange={setLockChannel}
          />
        </Field>
      </Row>
      <Box sx={{ mt: "24px" }}>
        <Field
          label="Notes"
          hint={
            lock?.lock_notes
              ? "Added below the existing lock notes; it does not replace them."
              : undefined
          }
        >
          <TextArea
            placeholder="Optional"
            value={confirmNotes}
            onChange={(e) => setConfirmNotes(e.target.value)}
          />
        </Field>
      </Box>
      <Box sx={{ mt: "22px" }}>
        <Callout tone="success">
          Confirming locks the loan, finalizes expiration, and emails the
          requester.
        </Callout>
      </Box>

      <Footer
        left={
          <GhostBtn onClick={() => setTab("return")}>
            <UndoOutlined /> Return To Requester
          </GhostBtn>
        }
        right={
          <PrimaryBtn
            color={T.green}
            loading={busy === "confirm"}
            disabled={busy && busy !== "confirm"}
            onClick={submitConfirm}
          >
            Confirm & Lock
            <LockOutlined className="thin" />
          </PrimaryBtn>
        }
      />
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════════
   EXTEND FIELDS — shared by Manage (desk) and the requester-only form.
   Requesting only records a PENDING extension; capital markets then
   confirms/denies it via the pending banner (two-step flow).
   ════════════════════════════════════════════════════════════════ */
const ExtendFields = ({ onExtend, busy, maxLockDays }) => {
  const anyBusy = busy != null;
  const [days, setDays] = useState(30);
  const [date, setDate] = useState(null);
  const [reason, setReason] = useState("");

  // Hide presets above the correspondent's max lock window (null = no cap). The
  // backend still enforces it on the cumulative lock; this just trims the UI.
  const periods = LOCK_PERIODS.filter(
    (d) => isNil(maxLockDays) || d <= maxLockDays,
  );

  return (
    <Box>
      <SectionLabel>Extend lock</SectionLabel>
      <Row>
        <Field label="Extension">
          <Segmented
            full
            options={periods.map((d) => ({ value: d, label: String(d) }))}
            value={days}
            onChange={(v) => {
              setDate(null);
              setDays(v);
            }}
          />
        </Field>
        <Field
          label="New expiration (optional)"
          hint="Overrides the preset above."
        >
          <DateInput value={date} onChange={setDate} />
        </Field>
      </Row>
      <Box sx={{ mt: "24px" }}>
        <Field label="Reason">
          <TextInput
            placeholder="Reason for extension"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </Box>
      <Box sx={{ mt: "16px" }}>
        <PrimaryBtn
          loading={busy === "extend"}
          disabled={anyBusy && busy !== "extend"}
          onClick={() =>
            onExtend(
              date
                ? { new_expiration_date: date, reason: reason || undefined }
                : { extension_days: days, reason: reason || undefined },
            )
          }
        >
          Request extension
        </PrimaryBtn>
      </Box>
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════════
   MANAGE (active lock) — extend / cancel for everyone (neither route is
   team-gated); pricing exception stays capital-markets only. A non-desk
   extension is a request the desk still confirms or denies.
   ════════════════════════════════════════════════════════════════ */
const ManageForm = ({
  onExtend,
  onCancelLock,
  onRequestException,
  busy,
  pendingExtension,
  maxLockDays,
  canConfirm,
  canCancel = true,
}) => {
  const anyBusy = busy != null;
  const [cancelReason, setCancelReason] = useState("");
  const [excCategory, setExcCategory] = useState("");
  const [excDescription, setExcDescription] = useState("");

  return (
    <Box>
      {pendingExtension ? (
        <Callout tone="warn">
          {canConfirm
            ? "An extension is pending confirmation (see above). Confirm or deny it before requesting another."
            : "An extension is pending confirmation by the capital-markets desk (see above)."}
        </Callout>
      ) : (
        <>
          {!canConfirm && (
            <Box sx={{ mb: "22px" }}>
              <Callout>
                The capital-markets desk reviews the computed fee and confirms
                or denies the extension.
              </Callout>
            </Box>
          )}
          <ExtendFields
            onExtend={onExtend}
            busy={busy}
            maxLockDays={maxLockDays}
          />
        </>
      )}

      {canCancel ? (
        <>
          <Divider />
          <SectionLabel>Cancel lock</SectionLabel>
          <Field label="Cancellation reason">
            <TextInput
              placeholder="Why is this lock being cancelled?"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </Field>
          <Box sx={{ mt: "16px" }}>
            <PrimaryBtn
              color={T.rose}
              loading={busy === "cancel"}
              disabled={anyBusy && busy !== "cancel"}
              onClick={() => onCancelLock(cancelReason)}
            >
              Cancel lock
            </PrimaryBtn>
          </Box>
        </>
      ) : null}

      {canConfirm && (
        <>
          <Divider />
          <SectionLabel>Pricing exception</SectionLabel>
          <Callout>
            File an exception when pricing needs review or the requester must
            provide an update. The requester is notified.
          </Callout>
          <Row sx={{ mt: "24px" }}>
            <Field label="Category">
              <TextInput
                placeholder="e.g. Rate exception"
                value={excCategory}
                onChange={(e) => setExcCategory(e.target.value)}
              />
            </Field>
            <Field label="Description">
              <TextInput
                placeholder="Describe the exception"
                value={excDescription}
                onChange={(e) => setExcDescription(e.target.value)}
              />
            </Field>
          </Row>
          <Box sx={{ mt: "16px" }}>
            <PrimaryBtn
              loading={busy === "exception"}
              disabled={
                (anyBusy && busy !== "exception") || !excDescription.trim()
              }
              onClick={() =>
                onRequestException({
                  pricingCategory: excCategory || "Pricing",
                  exceptionDescription: excDescription,
                })
              }
            >
              Request pricing exception
            </PrimaryBtn>
          </Box>
        </>
      )}
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════════
   EDIT DETAILS (active / confirmed lock) — amend price terms,
   expiration, lock type, pre-close review + notes in place via
   update_lock_details (whitelist-only). Distinct from Manage, which
   drives the lifecycle (extend / cancel / pricing exception).
   ════════════════════════════════════════════════════════════════ */
const EditDetailsForm = ({
  lock,
  loanDrilldown,
  product,
  onSubmit,
  onCancel,
  loading,
}) => {
  const isRtl = product === "rtl";
  const [f, setF] = useState(() => ({
    // DSCR
    rate_at_lock: decToPctStr(lock?.rate_at_lock),
    calculated_dscr_at_lock: isNil(lock?.calculated_dscr_at_lock)
      ? ""
      : String(lock.calculated_dscr_at_lock),
    margin_tier: lock?.margin_tier ?? "",
    base_price_for_rate: isNil(lock?.base_price_for_rate)
      ? ""
      : String(lock.base_price_for_rate),
    pricing_version_date: lock?.pricing_version_date ?? null,
    // RTL
    coupon_rate: decToPctStr(lock?.coupon_rate),
    pricing_base_rate: decToPctStr(lock?.pricing_base_rate),
    pricing_spread: decToPctStr(lock?.pricing_spread),
    risk_spread_adjustment: decToPctStr(lock?.risk_spread_adjustment),
    override_buy_rate: decToPctStr(lock?.override_buy_rate),
    borrower_classification_calculated:
      lock?.borrower_classification_calculated ?? "",
    // lock details
    lock_expiration_date: lock?.lock_expiration_date ?? null,
    // A legacy flag-only lock reads Broker here; setting Direct is the documented
    // way to correct one (rate-locks.md §"Lock channel").
    lock_channel:
      lockChannelOf(lock) ?? defaultLockChannelForDeal(loanDrilldown),
    preCloseReviewComplete: !!lock?.preCloseReviewComplete,
    lock_notes: lock?.lock_notes ?? "",
  }));
  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const submit = () => {
    const payload = {
      lock_expiration_date: f.lock_expiration_date ?? null,
      lock_channel: f.lock_channel,
      preCloseReviewComplete: !!f.preCloseReviewComplete,
      lock_notes: f.lock_notes || null,
    };
    if (isRtl) {
      Object.assign(payload, {
        coupon_rate: pctStrToDec(f.coupon_rate),
        pricing_base_rate: pctStrToDec(f.pricing_base_rate),
        pricing_spread: pctStrToDec(f.pricing_spread),
        risk_spread_adjustment: pctStrToDec(f.risk_spread_adjustment),
        override_buy_rate: pctStrToDec(f.override_buy_rate),
        borrower_classification_calculated:
          f.borrower_classification_calculated || null,
      });
    } else {
      Object.assign(payload, {
        rate_at_lock: pctStrToDec(f.rate_at_lock),
        calculated_dscr_at_lock: numStrToNum(f.calculated_dscr_at_lock),
        margin_tier: f.margin_tier || null,
        base_price_for_rate: numStrToNum(f.base_price_for_rate),
        pricing_version_date: f.pricing_version_date ?? null,
      });
    }
    // Don't wipe fields the desk left blank — drop nulls before sending.
    Object.keys(payload).forEach(
      (k) => payload[k] == null && delete payload[k],
    );
    onSubmit(payload);
  };

  return (
    <Box>
      <Callout>
        Update the locked terms without changing the lock’s status — its
        confirmation and expiration stay as they are. To extend, cancel, or file
        a pricing exception, use <strong>Manage lock</strong> instead.
      </Callout>

      <Box sx={{ mt: "22px" }}>
        <SectionLabel>Price terms</SectionLabel>
        {isRtl ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Row>
              <Field label="Coupon rate">
                <PercentInput
                  value={f.coupon_rate}
                  onChange={(v) => set("coupon_rate", v)}
                />
              </Field>
              <Field label="Pricing base rate">
                <PercentInput
                  value={f.pricing_base_rate}
                  onChange={(v) => set("pricing_base_rate", v)}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Pricing spread">
                <PercentInput
                  value={f.pricing_spread}
                  onChange={(v) => set("pricing_spread", v)}
                />
              </Field>
              <Field label="Risk spread adj.">
                <PercentInput
                  value={f.risk_spread_adjustment}
                  onChange={(v) => set("risk_spread_adjustment", v)}
                />
              </Field>
            </Row>
            <Row>
              <Field label="Borrower classification">
                <Select
                  value={f.borrower_classification_calculated}
                  muted={!f.borrower_classification_calculated}
                  onChange={(e) =>
                    set("borrower_classification_calculated", e.target.value)
                  }
                >
                  <option value="">Select classification</option>
                  {(RTL_BORROWER_CLASSIFICATIONS || []).map((o) => (
                    <option key={optVal(o)} value={optVal(o)}>
                      {optLabel(o)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Override buy rate (optional)">
                <PercentInput
                  value={f.override_buy_rate}
                  onChange={(v) => set("override_buy_rate", v)}
                />
              </Field>
            </Row>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Row>
              <Field label="Rate at lock">
                <PercentInput
                  value={f.rate_at_lock}
                  onChange={(v) => set("rate_at_lock", v)}
                />
              </Field>
              <Field label="Calculated DSCR">
                <TextInput
                  inputMode="decimal"
                  placeholder="0.00"
                  value={f.calculated_dscr_at_lock}
                  onChange={(e) =>
                    set("calculated_dscr_at_lock", e.target.value)
                  }
                />
              </Field>
            </Row>
            <Row>
              <Field label="Margin tier">
                <Select
                  value={f.margin_tier}
                  muted={!f.margin_tier}
                  onChange={(e) => set("margin_tier", e.target.value)}
                >
                  <option value="">Select tier</option>
                  {DSCR_MARGIN_TIERS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Base price (pts)">
                <TextInput
                  inputMode="decimal"
                  placeholder="0.000"
                  value={f.base_price_for_rate}
                  onChange={(e) => set("base_price_for_rate", e.target.value)}
                />
              </Field>
            </Row>
            <Field label="Pricing version date">
              <DateInput
                value={f.pricing_version_date}
                onChange={(v) => set("pricing_version_date", v)}
              />
            </Field>
          </Box>
        )}
      </Box>

      <Divider />
      <SectionLabel badge={<DeskBadge />}>Lock details</SectionLabel>
      <Row>
        <Field label="Expiration date" hint="The confirmed expiration.">
          <DateInput
            value={f.lock_expiration_date}
            onChange={(v) => set("lock_expiration_date", v)}
          />
        </Field>
        <Field label="Lock type">
          <Segmented
            options={LOCK_CHANNEL_OPTIONS}
            value={f.lock_channel}
            onChange={(v) => set("lock_channel", v)}
          />
        </Field>
      </Row>
      <Box sx={{ mt: "18px" }}>
        <Field label="Pre-close review">
          <Segmented
            options={[
              { value: false, label: "Not complete" },
              { value: true, label: "Complete" },
            ]}
            value={!!f.preCloseReviewComplete}
            onChange={(v) => set("preCloseReviewComplete", v)}
          />
        </Field>
      </Box>

      <Divider />
      <SectionLabel>Notes</SectionLabel>
      <Field label="Lock notes">
        <TextArea
          placeholder="Optional"
          value={f.lock_notes}
          onChange={(e) => set("lock_notes", e.target.value)}
        />
      </Field>

      <Footer
        left={<GhostBtn onClick={onCancel}>Cancel</GhostBtn>}
        right={
          <PrimaryBtn onClick={submit} loading={loading}>
            Save changes
          </PrimaryBtn>
        }
      />
    </Box>
  );
};

/* ════════════════════════════════════════════════════════════════
   SWITCHER
   ════════════════════════════════════════════════════════════════ */
const LoanLockFormV2 = ({
  loanDrilldown,
  onSuccess,
  onCancel,
  initialMode,
  // When true, the stage-1 "Returned to requester" callout is suppressed — the
  // host renders its own action-needed surface (e.g. the Lock Queue expanded row
  // shows it beside the form). Defaults false so the drilldown sidebar is unchanged.
  hideActionNeeded = false,
  // When true, the read-only "Lock notes" block is suppressed — the Lock Queue
  // expanded row renders its own notes card beside the form.
  hideLockNotes = false,
  // ({ variant: "panel" | "plain", view, body, footer }) → node. The drilldown
  // sidebar uses it to give the request form a pinned footer. Absent → inline.
  frame,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { isCapitalMarkets } = usePermissionsHook();
  const { getLoanById } = useUnderwritingHook();
  const correspondentsApi = useCorrespondentsApi();
  // Which action is in flight — so the spinner shows on the pressed button only.
  const [busy, setBusy] = useState(null);
  // Sub-mode for a requested lock: "edit" (desk amends price terms) or "update"
  // (requester re-submits → supersedes). null = the default confirm/return view.
  const [mode, setMode] = useState(initialMode || null);

  const dealId = loanDrilldown?._id;
  const lock = loanDrilldown?.loanLock;
  const stage = getLockStage(lock); // 0 none · 1 requested · 2 confirmed · 3 expired
  const canConfirm = isCapitalMarkets;
  const lockNotes = hideLockNotes ? null : (
    <LockNotesCard notes={lock?.lock_notes} />
  );

  // Resolve the loan's correspondent pricing to prepopulate the lock workflow:
  // max-lock window (trims period options), the extension-fee schedule, the DSCR
  // effective margin, and the resolved RTL product + spread. Final price still
  // resolves server-side at lock time; this is read-only context.
  const corrId = loanDrilldown?.correspondentCompanyId;
  const [corrPricing, setCorrPricing] = useState(null);
  const [tierMargins, setTierMargins] = useState(null);
  useEffect(() => {
    let alive = true;
    if (corrId) {
      correspondentsApi
        .effectivePricing(corrId)
        .then((p) => alive && setCorrPricing(p || null))
        .catch(() => {});
    } else {
      setCorrPricing(null);
    }
    correspondentsApi
      .listTierMargins()
      .then((list) => {
        if (!alive || !Array.isArray(list)) return;
        const m = {};
        list.forEach((d) => {
          const t = d._id ?? d.tier;
          if (t && d.marginPoints != null) m[t] = d.marginPoints;
        });
        setTierMargins(m);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [corrId, correspondentsApi]);

  const product =
    getLockProduct(loanDrilldown?.loanProductType) || lock?.product || "dscr";
  const rtlProductKey = rtlProductKeyFor(
    loanDrilldown?.loanProductType,
    lock?.rtl_product?.includes?.("heavy") ||
      loanDrilldown?.isHeavyRehab ||
      false,
  );
  const dscrTier = corrPricing?.dscr_pricing_tier || corrPricing?.pricingTier;
  const dscrAdditional = Number(corrPricing?.dscr_additional_margin || 0);
  const dscrOverride =
    corrPricing?.dscr_margin_override_points == null
      ? null
      : Number(corrPricing.dscr_margin_override_points);
  const tierMargin = dscrTier ? tierMargins?.[dscrTier] : null;
  const effectiveDscrMargin =
    dscrOverride != null
      ? dscrOverride
      : tierMargin != null
        ? tierMargin + dscrAdditional
        : null;

  const corrPricingNote = corrPricing ? (
    <CorrespondentPricingNote
      product={product}
      corrPricing={corrPricing}
      effectiveDscrMargin={effectiveDscrMargin}
      dscrTier={dscrTier}
      dscrAdditional={dscrAdditional}
      dscrOverride={dscrOverride}
      rtlProductKey={lock?.rtl_product || rtlProductKey}
    />
  ) : null;
  // Correspondent's max lock window — trims the lock-period presets across the
  // request + extend forms. null = no client-side cap (backend still enforces it).
  const maxLockDays = corrPricing?.max_lock_days ?? null;

  const refreshAnd = (msg) => {
    enqueueSnackbar(msg, { variant: "success" });
    if (dealId) {
      getLoanById({
        loanId: dealId,
        onSuccessFn: (deal) => {
          if (!isNil(deal)) dispatch(loanDrilldownSet(deal));
          onSuccess?.();
        },
      });
    } else {
      onSuccess?.();
    }
  };

  const run = async (action, promise, msg) => {
    setBusy(action);
    try {
      await promise;
      refreshAnd(msg);
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.detail || err?.message || "Lock action failed.",
        { variant: "error" },
      );
    } finally {
      setBusy(null);
    }
  };

  // Every branch below is BODY; the host frames it. A request / update / edit
  // renders LockRequestForm, which frames itself as a panel form (its footer is
  // pinned by the host); everything else is plain content.
  const renderBody = () => {
    // REQUEST — no active lock (none/cancelled/superseded)
    if (stage === 0) {
      return (
        <LockRequestForm
          mode="request"
          frame={frame}
          loanDrilldown={loanDrilldown}
          loading={busy === "request"}
          onCancel={onCancel}
          pricingNote={corrPricingNote}
          maxLockDays={maxLockDays}
          onSubmit={(product, payload, files) =>
            run(
              "request",
              product === "rtl"
                ? losLocksApi.requestRtlLock(dealId, payload, files)
                : losLocksApi.requestDscrLock(dealId, payload, files),
              "Lock requested.",
            )
          }
        />
      );
    }

    // RE-LOCK — the lock expired. "Re-lock" means a NEW lock request at today's
    // pricing (rate-locks.md: "Re-lock … is a new lock request"); the backend
    // accepts one because an Expired lock is no longer active. Extending the
    // expired lock is still offered, one click away (Request Board REQ-119).
    if (stage === 3 && mode !== "extend" && mode !== "edit") {
      const expiredOn = lock?.lock_expiration_date
        ? formatDate(lock.lock_expiration_date)
        : null;
      return (
        <LockRequestForm
          mode="request"
          frame={frame}
          loanDrilldown={loanDrilldown}
          loading={busy === "request"}
          onCancel={onCancel}
          pricingNote={
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Callout tone="warn">
                This lock expired{expiredOn ? ` on ${expiredOn}` : ""}. Request
                a new lock at today&apos;s pricing below, or{" "}
                <Box
                  component="button"
                  type="button"
                  onClick={() => setMode("extend")}
                  sx={{
                    p: 0,
                    border: 0,
                    bgcolor: "transparent",
                    font: "inherit",
                    fontWeight: 600,
                    color: "inherit",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  extend the expired lock
                </Box>{" "}
                instead.
              </Callout>
              {corrPricingNote}
            </Box>
          }
          maxLockDays={maxLockDays}
          onSubmit={(product, payload, files) =>
            run(
              "request",
              product === "rtl"
                ? losLocksApi.requestRtlLock(dealId, payload, files)
                : losLocksApi.requestDscrLock(dealId, payload, files),
              "New lock requested.",
            )
          }
        />
      );
    }

    // CONFIRM — status Requested
    if (stage === 1) {
      const actionNeeded =
        !hideActionNeeded && lock?.actionNeeded?.reason ? (
          <Box sx={{ mb: "20px" }}>
            <ActionNeededCallout action={lock.actionNeeded} />
          </Box>
        ) : null;

      // Requester (non-desk): resubmit an updated request, which supersedes.
      if (!canConfirm) {
        if (mode === "update") {
          return (
            <LockRequestForm
              mode="update"
              frame={frame}
              loanDrilldown={loanDrilldown}
              initialLock={lock}
              loading={busy === "request"}
              pricingNote={corrPricingNote}
              maxLockDays={maxLockDays}
              onCancel={() => setMode(null)}
              onSubmit={(product, payload, files) =>
                run(
                  "request",
                  product === "rtl"
                    ? losLocksApi.requestRtlLock(dealId, payload, files)
                    : losLocksApi.requestDscrLock(dealId, payload, files),
                  "Lock request updated.",
                )
              }
            />
          );
        }
        return (
          <Box>
            {actionNeeded}
            {lockNotes}
            <Callout tone="warn">
              This lock is awaiting confirmation by the capital-markets desk. Only
              capital markets can confirm or return a lock.
            </Callout>
            <Footer
              right={
                <PrimaryBtn onClick={() => setMode("update")}>
                  Update request
                </PrimaryBtn>
              }
            />
          </Box>
        );
      }

      // Desk "Edit details" — amend the price terms in place.
      if (mode === "edit") {
        return (
          <LockRequestForm
            mode="edit"
            frame={frame}
            loanDrilldown={loanDrilldown}
            initialLock={lock}
            loading={busy === "edit"}
            pricingNote={corrPricingNote}
            maxLockDays={maxLockDays}
            onCancel={() => setMode(null)}
            onSubmit={(_product, payload) =>
              run(
                "edit",
                losLocksApi.updateLockDetails(dealId, payload),
                "Lock details updated.",
              )
            }
          />
        );
      }

      return (
        <Box>
          {actionNeeded}
          {lockNotes}
          <ConfirmReturnForm
            lock={lock}
            loanDrilldown={loanDrilldown}
            busy={busy}
            onCancel={onCancel}
            onConfirm={(payload) =>
              run(
                "confirm",
                losLocksApi.confirmLock(dealId, payload),
                "Lock confirmed.",
              )
            }
            onReturn={(payload) =>
              run(
                "return",
                losLocksApi.returnLockForAction(dealId, payload),
                "Returned to requester.",
              )
            }
          />
        </Box>
      );
    }

    // MANAGE — confirmed/active.
    // A pending extension (set by request_lock_extension) shows a banner for
    // everyone; capital markets confirms or denies it from there.
    const pendingExtension = lock?.pendingExtension;
    const pendingBanner = pendingExtension ? (
      <Box sx={{ mb: "20px" }}>
        <PendingExtensionCallout
          pending={pendingExtension}
          canConfirm={canConfirm}
          busy={busy}
          onConfirm={(payload) =>
            run(
              "confirmExt",
              losLocksApi.confirmLockExtension(dealId, payload),
              "Lock extension confirmed.",
            )
          }
          onDeny={(payload) =>
            run(
              "denyExt",
              losLocksApi.confirmLockExtension(dealId, {
                deny: true,
                ...payload,
              }),
              "Lock extension denied.",
            )
          }
        />
      </Box>
    ) : null;

    const pricingNoteBlock = corrPricingNote ? (
      <Box sx={{ mb: "20px" }}>{corrPricingNote}</Box>
    ) : null;

    // "Edit details" — amend the locked terms in place (update_lock_details,
    // capital-markets only), distinct from Manage's lifecycle actions.
    if (mode === "edit" && canConfirm) {
      return (
        <Box>
          {pendingBanner}
          {pricingNoteBlock}
          <EditDetailsForm
            lock={lock}
            loanDrilldown={loanDrilldown}
            product={product}
            loading={busy === "edit"}
            onCancel={onCancel}
            onSubmit={(payload) =>
              run(
                "edit",
                losLocksApi.updateLockDetails(dealId, payload),
                "Lock details updated.",
              )
            }
          />
        </Box>
      );
    }
    return (
      <Box>
        {/* Extending an expired lock: the way back to a new request. */}
        {stage === 3 ? (
          <Box sx={{ mb: "20px" }}>
            <Callout tone="warn">
              This lock expired. Extend it below, or{" "}
              <Box
                component="button"
                type="button"
                onClick={() => setMode(null)}
                sx={{
                  p: 0,
                  border: 0,
                  bgcolor: "transparent",
                  font: "inherit",
                  fontWeight: 600,
                  color: "inherit",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                request a new lock
              </Box>{" "}
              at today&apos;s pricing instead.
            </Callout>
          </Box>
        ) : null}
        {pendingBanner}
        {pricingNoteBlock}
        {lockNotes}
        <ManageForm
          busy={busy}
          pendingExtension={pendingExtension}
          maxLockDays={maxLockDays}
          canConfirm={canConfirm}
          // An Expired lock can't be cancelled (the backend answers 409 —
          // nothing active to cancel); a lapsed one the sweep hasn't marked yet can.
          canCancel={lock?.lock_status !== "Expired"}
          onExtend={(payload) =>
            run(
              "extend",
              losLocksApi.requestLockExtension(dealId, payload),
              "Lock extension requested.",
            )
          }
          onCancelLock={(reason) =>
            run(
              "cancel",
              losLocksApi.cancelLock(dealId, reason),
              "Lock cancelled.",
            )
          }
          onRequestException={(payload) =>
            run(
              "exception",
              losLocksApi.requestPricingException(dealId, payload),
              "Pricing exception requested.",
            )
          }
        />
      </Box>
    );
  };

  const body = renderBody();
  if (!frame || (isValidElement(body) && body.type === LockRequestForm)) {
    return body;
  }
  return frame({ variant: "plain", body });
};

export default LoanLockFormV2;
